/**
 * Created by Ning Jiang on 3/28/2017.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {
    ISymbolStateConfig,
    ISymbolStates,
    StateReelSymbol,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {WixxHelper} from "../utils/WixxHelper";
import {WixxGameConfig} from "../WixxGameConfig";

export interface IWixxSymbolStates extends ISymbolStates {
    readonly boost:ISymbolStateConfig;
    readonly attention:ISymbolStateConfig;
}

export class WixxReelSymbol extends StateReelSymbol {

    private _multiplier:PIXI.Container;
    private _multiplier_normal:TimelineSprite;
    private _multiplier_spin:PIXI.Sprite;
    private _wildAttention:TimelineSprite;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig) {
        super(symName, reelId, state);
    }

    protected reset(remove:boolean):void {
        super.reset(remove);

        this.removeAttentionAnimation();

        if(remove) {
            this.removeMultiplier();
        }
    }

    protected getStateChangingAnimation(transitionConfig:SymbolStateTransition):Timeline {
        this.removeAttentionAnimation();

        if(WixxHelper.isBoostedSymbol(this.symName)) {
            switch(transitionConfig.state.keyword) {
                case WixxGameConfig.instance.SYMBOL_STATES.idle!.keyword:
                    transitionConfig.frames = [49, 28, 49];
                    break;
                case WixxGameConfig.instance.SYMBOL_STATES.win.keyword:
                    transitionConfig.frames = [49, 15, 49];
                    break;
                default:
                // Do nothing.
            }
        }

        if(transitionConfig.state.keyword === WixxGameConfig.instance.SYMBOL_STATES.noWin!.keyword) {
            transitionConfig.alpha = 0.3;
        }
        return super.getStateChangingAnimation(transitionConfig);
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(this._multiplier && this._multiplier.visible) {
            timeline.add(this.playMultiplier(
                newState.state.keyword === GameConfig.instance.SYMBOL_STATES.noWin!.keyword ? WixxGameConfig.instance.SYMBOL_MULTIPLIER_NO_WIN_ALPHA : 1,
                newState.fadeInDuration,
                newState.state.keyword === GameConfig.instance.SYMBOL_STATES.win.keyword ? 0 : (newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword ? 2 : 1)
            ), delay);
        }

        if((this.symName === ReelSymbolName.WILD || this.symName === "M1_boosted") && newState.state.keyword === WixxGameConfig.instance.SYMBOL_STATES.attention.keyword) {
            timeline.add(this.playWildAttentionAnimation(), delay);
        }
    }

    private initMultiplier():void {
        if(this._multiplier) {
            return;
        }

        this._multiplier = new PIXI.Container();
        this._multiplier.alpha = 0;
        this._multiplier.visible = false;
        this.addChild(this._multiplier);

        this._multiplier_normal = new TimelineSprite(GameResources.getTextures("x5"));
        this._multiplier_normal.anchor.set(0.5, 0.5);
        this._multiplier_normal.alpha = 0;
        this._multiplier.addChild(this._multiplier_normal);

        this._multiplier_spin = new PIXI.Sprite(GameResources.getTextures("x5_spin")[0]);
        this._multiplier_spin.anchor.set(0.5, 0.5);
        this._multiplier_spin.alpha = 0;
        this._multiplier.addChild(this._multiplier_spin);
    }

    // @param animation 0=play animation, 1=play still image, 2 = play spin.
    public playMultiplier(alpha:number = 1, alphaDuration:number = 0, animation:0 | 1 | 2 = 0):Timeline {
        this.initMultiplier();

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._multiplier.visible = true;
            },
            TweenLite.to(this._multiplier, alphaDuration, {alpha : alpha})
        ]);

        switch(animation) {
            case 0:
                timeline.add([
                    TweenLite.to(this._multiplier_spin, alphaDuration, {alpha : 0}),
                    TweenLite.to(this._multiplier_normal, alphaDuration, {alpha : 1}),
                    this._multiplier_normal.getAnimation()
                ], 0);
                break;
            case 1:
                timeline.add([
                    TweenLite.to(this._multiplier_spin, alphaDuration, {alpha : 0}),
                    TweenLite.to(this._multiplier_normal, alphaDuration, {alpha : 1}),
                    this._multiplier_normal.getAnimation([-1, -1])
                ], 0);
                break;
            case 2:
                timeline.add([
                    TweenLite.to(this._multiplier_spin, alphaDuration, {alpha : 1}),
                    TweenLite.to(this._multiplier_normal, alphaDuration, {alpha : 0}),
                ], 0);
                break;
            default:
                debugger;
                throw new Error("WixxReelSymbol.playMultiplier(): Wrong animation state!");
        }

        return timeline;
    }

    public hideMultiplier(fadeOutDuration:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();

        if(this._multiplier != null) {
            timeline.add(TweenLite.to(this._multiplier, fadeOutDuration, {alpha : 0}));
            timeline.add(() => {
                this._multiplier.visible = false;
                this._multiplier_normal.alpha = 0;
                this._multiplier_spin.alpha = 0;
            });
        }

        return timeline;
    }

    private removeMultiplier():void {
        if(this._multiplier) {
            this._multiplier_normal.alpha = 0;
            this._multiplier_spin.alpha = 0;
            this._multiplier.alpha = 0;
            this._multiplier.visible = false;
        }
    }

    private initWildAttention():void {
        if(this._wildAttention) {
            return;
        }

        this._wildAttention = new TimelineSprite(GameResources.getTextures("W_attention"));
        this._wildAttention.anchor.set(0.5, 0.5);
        this._wildAttention.hide();
        this.addChild(this._wildAttention);
    }

    public playWildAttentionAnimation():Timeline {
        this.initWildAttention();

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {this._wildAttention.show()},
            this._wildAttention.getAnimation(),
        ]);
        timeline.add(() => {
            this.removeAttentionAnimation();
        });
        return timeline;
    }

    private removeAttentionAnimation():void {
        if(this._wildAttention) {
            this._wildAttention.hide();
        }
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        if(super.initState(stateConfig)) {
            // To move the patches always on the top.
            if(this._multiplier) {
                this.addChild(this._multiplier);
            }

            if(this._wildAttention) {
                this.addChild(this._wildAttention);
            }

            return true;
        }

        return false;
    }
}